import React, { useState, useEffect, useRef } from 'react';

/**
 * Autocomplete Selector Component
 * Allows searching and selecting items from a list with multi-select support
 */
export const AutocompleteSelector = ({ 
  label,
  placeholder = "Search...",
  selectedIds = [],
  onChange,
  fetchItems,
  itemLabelKey = "name",
  itemIdKey = "id",
  helperText
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch all items on mount
  useEffect(() => {
    loadItems();
  }, []);

  // Filter items based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredItems(allItems.filter(item => !selectedIds.includes(item[itemIdKey])));
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = allItems.filter(item => 
        item[itemLabelKey].toLowerCase().includes(query) && 
        !selectedIds.includes(item[itemIdKey])
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery, allItems, selectedIds, itemLabelKey, itemIdKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const items = await fetchItems();
      setAllItems(items);
      setFilteredItems(items.filter(item => !selectedIds.includes(item[itemIdKey])));
    } catch (err) {
      console.error('Failed to load items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (itemId) => {
    onChange([...selectedIds, itemId]);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleRemoveItem = (itemId) => {
    onChange(selectedIds.filter(id => id !== itemId));
  };

  const getSelectedItems = () => {
    return selectedIds.map(id => allItems.find(item => item[itemIdKey] === id)).filter(Boolean);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-[#111827]">{label}</label>
      
      {/* Selected Items Display */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {getSelectedItems().map(item => (
            <div 
              key={item[itemIdKey]} 
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1E40AF] text-white rounded-lg text-sm"
            >
              <span>{item[itemLabelKey]}</span>
              <button
                type="button"
                onClick={() => handleRemoveItem(item[itemIdKey])}
                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative" ref={dropdownRef}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full px-4 py-2 border border-[#D1D5DB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
        />

        {/* Dropdown List */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-[#D1D5DB] rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-3 text-center text-[#6B7280]">Loading...</div>
            ) : filteredItems.length === 0 ? (
              <div className="px-4 py-3 text-center text-[#6B7280]">
                {searchQuery ? 'No results found' : 'All items selected'}
              </div>
            ) : (
              <ul>
                {filteredItems.slice(0, 10).map(item => (
                  <li 
                    key={item[itemIdKey]}
                    onClick={() => handleAddItem(item[itemIdKey])}
                    className="px-4 py-2 hover:bg-[#F3F4F6] cursor-pointer transition-colors border-b border-[#E5E7EB] last:border-b-0"
                  >
                    <div className="text-sm font-medium text-[#111827]">{item[itemLabelKey]}</div>
                    {item.description && (
                      <div className="text-xs text-[#6B7280] mt-0.5">{item.description}</div>
                    )}
                  </li>
                ))}
                {filteredItems.length > 10 && (
                  <li className="px-4 py-2 text-xs text-[#6B7280] text-center bg-[#F9FAFB]">
                    Showing 10 of {filteredItems.length} results. Keep typing to narrow down.
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>

      {helperText && (
        <p className="text-xs text-[#6B7280]">{helperText}</p>
      )}
    </div>
  );
};
